import React, { useState } from 'react';
import { Calendar, DollarSign, MapPin } from 'lucide-react';
interface Event {
  id: string;
  name: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  proposedEventDate: string;
  total: number;
  selections: {
    fullGen3Ring: number;
    isTwoDayEvent?: boolean;
  };
}
interface USMapProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
}
export function USMap({
  events,
  onEventClick
}: USMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0
  });
  // Helper function to get actual ring count (divide by 2 for two-day events)
  const getActualRingCount = (event: Event) => {
    if (event.selections.isTwoDayEvent) {
      return Math.round(event.selections.fullGen3Ring / 2);
    }
    return event.selections.fullGen3Ring;
  };
  // Group events by state
  const eventsByState = events.reduce((acc, event) => {
    const state = event.customerInfo?.state;
    if (state) {
      const stateAbbr = normalizeStateAbbreviation(state);
      if (stateAbbr) {
        if (!acc[stateAbbr]) {
          acc[stateAbbr] = {
            count: 0,
            rings: 0,
            events: []
          };
        }
        acc[stateAbbr].count++;
        acc[stateAbbr].rings += getActualRingCount(event);
        acc[stateAbbr].events.push(event);
      }
    }
    return acc;
  }, {} as Record<string, {
    count: number;
    rings: number;
    events: Event[];
  }>);
  console.log('Events by state:', eventsByState); // Debug log to see what's being calculated
  // Normalize state to 2-letter abbreviation
  function normalizeStateAbbreviation(state: string): string | null {
    const stateAbbreviations: Record<string, string> = {
      alabama: 'AL',
      al: 'AL',
      alaska: 'AK',
      ak: 'AK',
      arizona: 'AZ',
      az: 'AZ',
      arkansas: 'AR',
      ar: 'AR',
      california: 'CA',
      ca: 'CA',
      colorado: 'CO',
      co: 'CO',
      connecticut: 'CT',
      ct: 'CT',
      delaware: 'DE',
      de: 'DE',
      florida: 'FL',
      fl: 'FL',
      georgia: 'GA',
      ga: 'GA',
      hawaii: 'HI',
      hi: 'HI',
      idaho: 'ID',
      id: 'ID',
      illinois: 'IL',
      il: 'IL',
      indiana: 'IN',
      in: 'IN',
      iowa: 'IA',
      ia: 'IA',
      kansas: 'KS',
      ks: 'KS',
      kentucky: 'KY',
      ky: 'KY',
      louisiana: 'LA',
      la: 'LA',
      maine: 'ME',
      me: 'ME',
      maryland: 'MD',
      md: 'MD',
      massachusetts: 'MA',
      ma: 'MA',
      michigan: 'MI',
      mi: 'MI',
      minnesota: 'MN',
      mn: 'MN',
      mississippi: 'MS',
      ms: 'MS',
      missouri: 'MO',
      mo: 'MO',
      montana: 'MT',
      mt: 'MT',
      nebraska: 'NE',
      ne: 'NE',
      nevada: 'NV',
      nv: 'NV',
      'new hampshire': 'NH',
      nh: 'NH',
      'new jersey': 'NJ',
      nj: 'NJ',
      'new mexico': 'NM',
      nm: 'NM',
      'new york': 'NY',
      ny: 'NY',
      'north carolina': 'NC',
      nc: 'NC',
      'north dakota': 'ND',
      nd: 'ND',
      ohio: 'OH',
      oh: 'OH',
      oklahoma: 'OK',
      ok: 'OK',
      oregon: 'OR',
      or: 'OR',
      pennsylvania: 'PA',
      pa: 'PA',
      'rhode island': 'RI',
      ri: 'RI',
      'south carolina': 'SC',
      sc: 'SC',
      'south dakota': 'SD',
      sd: 'SD',
      tennessee: 'TN',
      tn: 'TN',
      texas: 'TX',
      tx: 'TX',
      utah: 'UT',
      ut: 'UT',
      vermont: 'VT',
      vt: 'VT',
      virginia: 'VA',
      va: 'VA',
      washington: 'WA',
      wa: 'WA',
      'west virginia': 'WV',
      wv: 'WV',
      wisconsin: 'WI',
      wi: 'WI',
      wyoming: 'WY',
      wy: 'WY'
    };
    const normalized = state.toLowerCase().trim();
    return stateAbbreviations[normalized] || null;
  }
  // Extract state from legacy location string (for backwards compatibility)
  function extractStateFromLegacyData(event: any): string | null {
    // Check if event has old shippingLocation field
    if (event.shippingLocation) {
      const stateAbbreviations: Record<string, string> = {
        alabama: 'AL',
        al: 'AL',
        alaska: 'AK',
        ak: 'AK',
        arizona: 'AZ',
        az: 'AZ',
        arkansas: 'AR',
        ar: 'AR',
        california: 'CA',
        ca: 'CA',
        colorado: 'CO',
        co: 'CO',
        connecticut: 'CT',
        ct: 'CT',
        delaware: 'DE',
        de: 'DE',
        florida: 'FL',
        fl: 'FL',
        georgia: 'GA',
        ga: 'GA',
        hawaii: 'HI',
        hi: 'HI',
        idaho: 'ID',
        id: 'ID',
        illinois: 'IL',
        il: 'IL',
        indiana: 'IN',
        in: 'IN',
        iowa: 'IA',
        ia: 'IA',
        kansas: 'KS',
        ks: 'KS',
        kentucky: 'KY',
        ky: 'KY',
        louisiana: 'LA',
        la: 'LA',
        maine: 'ME',
        me: 'ME',
        maryland: 'MD',
        md: 'MD',
        massachusetts: 'MA',
        ma: 'MA',
        michigan: 'MI',
        mi: 'MI',
        minnesota: 'MN',
        mn: 'MN',
        mississippi: 'MS',
        ms: 'MS',
        missouri: 'MO',
        mo: 'MO',
        montana: 'MT',
        mt: 'MT',
        nebraska: 'NE',
        ne: 'NE',
        nevada: 'NV',
        nv: 'NV',
        'new hampshire': 'NH',
        nh: 'NH',
        'new jersey': 'NJ',
        nj: 'NJ',
        'new mexico': 'NM',
        nm: 'NM',
        'new york': 'NY',
        ny: 'NY',
        'north carolina': 'NC',
        nc: 'NC',
        'north dakota': 'ND',
        nd: 'ND',
        ohio: 'OH',
        oh: 'OH',
        oklahoma: 'OK',
        ok: 'OK',
        oregon: 'OR',
        or: 'OR',
        pennsylvania: 'PA',
        pa: 'PA',
        'rhode island': 'RI',
        ri: 'RI',
        'south carolina': 'SC',
        sc: 'SC',
        'south dakota': 'SD',
        sd: 'SD',
        tennessee: 'TN',
        tn: 'TN',
        texas: 'TX',
        tx: 'TX',
        utah: 'UT',
        ut: 'UT',
        vermont: 'VT',
        vt: 'VT',
        virginia: 'VA',
        va: 'VA',
        washington: 'WA',
        wa: 'WA',
        'west virginia': 'WV',
        wv: 'WV',
        wisconsin: 'WI',
        wi: 'WI',
        wyoming: 'WY',
        wy: 'WY'
      };
      const loc = event.shippingLocation.toLowerCase();
      for (const [key, value] of Object.entries(stateAbbreviations)) {
        if (loc.includes(key)) {
          return value;
        }
      }
    }
    return null;
  }
  // Get color intensity based on ring count
  function getStateColor(rings: number): string {
    if (rings === 0) return '#f1f5f9'; // slate-100
    if (rings <= 2) return '#dbeafe'; // blue-100
    if (rings <= 5) return '#93c5fd'; // blue-300
    if (rings <= 10) return '#3b82f6'; // blue-500
    return '#1e40af'; // blue-800
  }
  // State positions (approximate center coordinates)
  const statePositions: Record<string, {
    x: number;
    y: number;
  }> = {
    AL: {
      x: 680,
      y: 520
    },
    AK: {
      x: 150,
      y: 650
    },
    AZ: {
      x: 220,
      y: 480
    },
    AR: {
      x: 580,
      y: 480
    },
    CA: {
      x: 120,
      y: 400
    },
    CO: {
      x: 340,
      y: 380
    },
    CT: {
      x: 880,
      y: 280
    },
    DE: {
      x: 850,
      y: 340
    },
    FL: {
      x: 780,
      y: 600
    },
    GA: {
      x: 740,
      y: 520
    },
    HI: {
      x: 280,
      y: 650
    },
    ID: {
      x: 220,
      y: 240
    },
    IL: {
      x: 620,
      y: 360
    },
    IN: {
      x: 680,
      y: 360
    },
    IA: {
      x: 560,
      y: 320
    },
    KS: {
      x: 480,
      y: 400
    },
    KY: {
      x: 700,
      y: 400
    },
    LA: {
      x: 580,
      y: 560
    },
    ME: {
      x: 900,
      y: 180
    },
    MD: {
      x: 830,
      y: 360
    },
    MA: {
      x: 890,
      y: 260
    },
    MI: {
      x: 680,
      y: 280
    },
    MN: {
      x: 540,
      y: 220
    },
    MS: {
      x: 620,
      y: 520
    },
    MO: {
      x: 580,
      y: 400
    },
    MT: {
      x: 300,
      y: 200
    },
    NE: {
      x: 440,
      y: 340
    },
    NV: {
      x: 180,
      y: 360
    },
    NH: {
      x: 890,
      y: 240
    },
    NJ: {
      x: 860,
      y: 320
    },
    NM: {
      x: 320,
      y: 480
    },
    NY: {
      x: 840,
      y: 260
    },
    NC: {
      x: 780,
      y: 460
    },
    ND: {
      x: 440,
      y: 200
    },
    OH: {
      x: 720,
      y: 360
    },
    OK: {
      x: 480,
      y: 460
    },
    OR: {
      x: 140,
      y: 220
    },
    PA: {
      x: 800,
      y: 320
    },
    RI: {
      x: 900,
      y: 280
    },
    SC: {
      x: 760,
      y: 500
    },
    SD: {
      x: 440,
      y: 260
    },
    TN: {
      x: 680,
      y: 460
    },
    TX: {
      x: 440,
      y: 540
    },
    UT: {
      x: 260,
      y: 360
    },
    VT: {
      x: 880,
      y: 230
    },
    VA: {
      x: 800,
      y: 400
    },
    WA: {
      x: 140,
      y: 160
    },
    WV: {
      x: 760,
      y: 380
    },
    WI: {
      x: 620,
      y: 260
    },
    WY: {
      x: 320,
      y: 280
    }
  };
  return <div className="space-y-6" data-id="element-1867">
      <div className="flex flex-col gap-4" data-id="element-1868">
        <div data-id="element-1869">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" data-id="element-1870">
            Event Distribution Map
          </h2>
          <p className="text-sm text-slate-600" data-id="element-1871">
            Visual overview of events and ring counts across the United States.
            Hover over states to see event details.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm" data-id="element-1872">
          <div className="flex items-center gap-2" data-id="element-1873">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#dbeafe'
          }} data-id="element-1874"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-1875">1-2 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1876">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#93c5fd'
          }} data-id="element-1877"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-1878">3-5 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1879">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#3b82f6'
          }} data-id="element-1880"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-1881">6-10 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1882">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#1e40af'
          }} data-id="element-1883"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-1884">10+ rings</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-8 relative overflow-x-auto" data-id="element-1885">
        <svg viewBox="0 0 1000 700" className="w-full h-auto" onMouseLeave={() => setHoveredState(null)} data-id="element-1886">
          {/* Background */}
          <rect width="1000" height="700" fill="#f8fafc" data-id="element-1887" />

          {/* State markers */}
          {Object.entries(statePositions).map(([state, pos]) => {
          const data = eventsByState[state];
          const rings = data?.rings || 0;
          const count = data?.count || 0;
          const color = getStateColor(rings);
          // Calculate total rings for this state
          const totalRings = data ? data.events.reduce((sum, event) => sum + getActualRingCount(event), 0) : 0;
          return <g key={state} onMouseEnter={e => {
            if (totalRings > 0) {
              setHoveredState(state);
              const svg = e.currentTarget.ownerSVGElement;
              if (svg) {
                const rect = svg.getBoundingClientRect();
                const svgX = pos.x / 1000 * rect.width;
                const svgY = pos.y / 700 * rect.height;
                setTooltipPosition({
                  x: rect.left + svgX,
                  y: rect.top + svgY
                });
              }
            }
          }} style={{
            cursor: totalRings > 0 ? 'pointer' : 'default'
          }} data-id="element-1888">
                {/* State circle */}
                <circle cx={pos.x} cy={pos.y} r={totalRings > 0 ? Math.max(25, Math.min(60, totalRings * 3)) : 15} fill={color} stroke="#1e293b" strokeWidth={hoveredState === state ? '2' : '1'} opacity={totalRings > 0 ? hoveredState === state ? 1 : 0.9 : 0.3} style={{
              transition: 'all 0.2s ease'
            }} data-id="element-1889" />

                {/* State abbreviation */}
                <text x={pos.x} y={pos.y - 8} textAnchor="middle" fontSize="13" fontWeight="700" fill={totalRings > 5 ? '#ffffff' : '#1e293b'} style={{
              pointerEvents: 'none'
            }} data-id="element-1890">
                  {state}
                </text>

                {/* Total ring count ONLY - single text element */}
                {totalRings > 0 && <text x={pos.x} y={pos.y + 6} textAnchor="middle" fontSize="16" fontWeight="800" fill={totalRings > 5 ? '#ffffff' : '#1e40af'} style={{
              pointerEvents: 'none'
            }} data-id="element-1891">
                    {totalRings}R
                  </text>}
              </g>;
        })}
        </svg>

        {/* Tooltip */}
        {hoveredState && eventsByState[hoveredState] && <div className="fixed z-50 bg-white rounded-lg shadow-2xl border border-slate-200 p-3 sm:p-4 max-w-[90vw] sm:max-w-md" style={{
        left: `${Math.min(tooltipPosition.x + 20, window.innerWidth - 320)}px`,
        top: `${Math.max(20, tooltipPosition.y - 100)}px`,
        pointerEvents: 'none'
      }} data-id="element-1892">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3" data-id="element-1893">
              <h4 className="font-semibold text-slate-800 text-base sm:text-lg" data-id="element-1894">
                {hoveredState}
              </h4>
              <div className="text-xs sm:text-sm text-slate-600" data-id="element-1895">
                {eventsByState[hoveredState].count} event
                {eventsByState[hoveredState].count !== 1 ? 's' : ''} •{' '}
                {eventsByState[hoveredState].rings} ring
                {eventsByState[hoveredState].rings !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto" data-id="element-1896">
              {eventsByState[hoveredState].events.map(event => <div key={event.id} className="p-2 sm:p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border border-slate-200" onClick={e => {
            e.stopPropagation();
            if (onEventClick) {
              onEventClick(event);
            }
          }} style={{
            pointerEvents: 'auto'
          }} data-id="element-1897">
                  <div className="font-medium text-slate-800 mb-1 text-sm break-words" data-id="element-1898">
                    {event.eventName}
                  </div>
                  <div className="text-xs text-slate-600 space-y-1" data-id="element-1899">
                    <div className="flex items-center gap-1" data-id="element-1900">
                      <MapPin size={12} className="flex-shrink-0" data-id="element-1901" />
                      <span className="break-words" data-id="element-1902">{event.name}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1" data-id="element-1903">
                      <div className="flex items-center gap-1" data-id="element-1904">
                        <Calendar size={12} className="flex-shrink-0" data-id="element-1905" />
                        <span data-id="element-1906">
                          {event.proposedEventDate ? new Date(event.proposedEventDate).toLocaleDateString() : 'No date'}
                        </span>
                        {event.selections.isTwoDayEvent && <span className="text-purple-600 text-xs font-medium" data-id="element-1907">
                            (2d)
                          </span>}
                      </div>
                      <div className="flex items-center gap-1" data-id="element-1908">
                        <span className="font-semibold text-blue-600" data-id="element-1909">
                          {getActualRingCount(event)}R
                        </span>
                        <span className="text-slate-500" data-id="element-1910">•</span>
                        <DollarSign size={12} className="flex-shrink-0" data-id="element-1911" />
                        <span className="font-semibold" data-id="element-1912">
                          ${event.total.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="mt-2 text-xs text-slate-500 text-center" data-id="element-1913">
              Click on an event to view full details
            </div>
          </div>}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-1914">
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-1915">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-1916">
            Total Events
          </div>
          <div className="text-xl sm:text-2xl font-bold text-slate-800" data-id="element-1917">
            {events.length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-1918">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-1919">
            Total Rings
          </div>
          <div className="text-xl sm:text-2xl font-bold text-blue-600" data-id="element-1920">
            {events.reduce((sum, e) => sum + getActualRingCount(e), 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-1921">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-1922">
            States Covered
          </div>
          <div className="text-xl sm:text-2xl font-bold text-green-600" data-id="element-1923">
            {Object.keys(eventsByState).length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-1924">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-1925">
            Avg Rings/Event
          </div>
          <div className="text-xl sm:text-2xl font-bold text-purple-600" data-id="element-1926">
            {events.length > 0 ? (events.reduce((sum, e) => sum + getActualRingCount(e), 0) / events.length).toFixed(1) : '0'}
          </div>
        </div>
      </div>

      {/* State Breakdown Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-1927">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-slate-50" data-id="element-1928">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800" data-id="element-1929">
            State Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-1930">
          <table className="w-full" data-id="element-1931">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1932">
              <tr data-id="element-1933">
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-1934">
                  State
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-1935">
                  Events
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-1936">
                  Rings
                </th>
                <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-1937">
                  Event Names
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-1938">
              {Object.entries(eventsByState).sort((a, b) => b[1].rings - a[1].rings).map(([state, data]) => <tr key={state} className="hover:bg-slate-50" data-id="element-1939">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-1940">
                      <span className="font-semibold text-slate-800" data-id="element-1941">
                        {state}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-slate-600" data-id="element-1942">
                      {data.count}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right" data-id="element-1943">
                      <span className="font-semibold text-blue-600" data-id="element-1944">
                        {data.rings}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-sm text-slate-600" data-id="element-1945">
                      {data.events.map(e => e.eventName).join(', ')}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}